import type { ReactNode } from 'react';
import { Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import { tokens } from '@/styles/tokens';
import { ReactComponent as CirclePlusDashed } from '@/assets/icons/circle-plus-dashed.svg';

/**
 * Shared marker/tickmark picker content (Figma 36760-157545): a row of glyph
 * tickmarks, a divider, then the numbered notes and a dashed "add note" button,
 * wrapping within the tickmark row's width.
 * Purely presentational — the caller supplies the rendered glyphs/badges and the
 * selection callbacks — so the Editor annotation tools and the Data-analysis cell
 * marker picker render from ONE component. Drop it inside any popover/dropdown.
 */

export interface TickmarkPickerSymbol {
  id: string;
  /** Rendered glyph (e.g. <TickmarkGlyph … />). */
  glyph: ReactNode;
  /** Tooltip / aria label (the mark's default meaning). */
  label?: string;
  /** Active mark gets the grey selection box (Editor tool selection). */
  active?: boolean;
}

export interface TickmarkPickerNote {
  id: string;
  /** Rendered badge (e.g. <NoteBadge … />). */
  badge: ReactNode;
  label?: string;
  active?: boolean;
}

export interface TickmarkPickerProps {
  symbols: TickmarkPickerSymbol[];
  notes: TickmarkPickerNote[];
  onSelectSymbol: (id: string) => void;
  onSelectNote: (id: string) => void;
  /** The dashed circle-plus — mints/adds the next note. Omit to hide it. */
  onAddNote?: () => void;
  addNoteLabel?: string;
  'aria-label'?: string;
  'data-testid'?: string;
}

const Root = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  // As wide as the tickmark row in any host, however wide the host is (A-25).
  width: 'fit-content',
  gap: 8,
  // 8px between the button rows and the popover border (Figma).
  padding: 8,
});

const Row = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
});

// The notes never widen the picker (A-25): this row takes no width of its own and fills the
// tickmark row's, so the notes and `+` wrap onto further rows instead of growing it sideways.
const NoteRow = styled(Row)({
  flexWrap: 'wrap',
  width: 0,
  minWidth: '100%',
});

const Item = styled('button', {
  shouldForwardProp: (p) => p !== 'active',
})<{ active?: boolean }>(({ active }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 32,
  height: 32,
  padding: 0,
  border: 'none',
  borderRadius: 8,
  background: active ? tokens.grey[100] : 'transparent',
  color: tokens.grey[50],
  cursor: 'pointer',
  transition: 'background-color 0.15s ease',
  '&:hover': { backgroundColor: tokens.grey[100] },
}));

const Divider = styled('div')({
  height: 1,
  backgroundColor: tokens.border.light,
  margin: '0 4px',
});

export function TickmarkPicker({
  symbols,
  notes,
  onSelectSymbol,
  onSelectNote,
  onAddNote,
  addNoteLabel = 'Add note',
  ...rest
}: TickmarkPickerProps) {
  const showNoteRow = notes.length > 0 || Boolean(onAddNote);

  return (
    <Root role='menu' aria-label={rest['aria-label']} data-testid={rest['data-testid']}>
      <Row>
        {symbols.map((symbol) => (
          <Tooltip key={symbol.id} title={symbol.label ?? ''} arrow disableInteractive>
            <Item
              type='button'
              active={symbol.active}
              onClick={() => onSelectSymbol(symbol.id)}
              aria-label={symbol.label ?? symbol.id}
              data-testid={`tickmark-symbol-${symbol.id}`}
            >
              {symbol.glyph}
            </Item>
          </Tooltip>
        ))}
      </Row>

      {showNoteRow && (
        <>
          <Divider />
          <NoteRow data-testid='tickmark-note-row'>
            {notes.map((note) => (
              <Tooltip key={note.id} title={note.label ?? ''} arrow disableInteractive>
                <Item
                  type='button'
                  active={note.active}
                  onClick={() => onSelectNote(note.id)}
                  aria-label={note.label ?? note.id}
                  data-testid={`tickmark-note-${note.id}`}
                >
                  {note.badge}
                </Item>
              </Tooltip>
            ))}
            {onAddNote && (
              <Item
                type='button'
                onClick={onAddNote}
                aria-label={addNoteLabel}
                data-testid='tickmark-add-note'
              >
                <CirclePlusDashed width={18} height={18} aria-hidden />
              </Item>
            )}
          </NoteRow>
        </>
      )}
    </Root>
  );
}
